/**
 * Reporter — Collection.
 *
 * The screen that answers one question: who still owes us a number, and can
 * this quarter go to CMS yet?
 *
 * The old flow asked for a whole report in one file, which no single vendor can
 * produce. This one tracks each CMS element separately, so a UM vendor who has
 * done their eight elements reads as complete even while claims and appeals are
 * still outstanding — and the submission is held until every element has an
 * answer from whoever owns it.
 *
 * Hidden entirely unless the backend feature flag is on: /catalogue 404s, and
 * the tab never appears.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_API_URL;

const MEASURES = [
  { id: 'odr', label: 'Organization Determinations & Reconsiderations' },
  { id: 'grievances_c', label: 'Grievances — Part C' },
  { id: 'grievances_d', label: 'Grievances — Part D' },
];

const STATE_STYLE = {
  answered:       { label: 'Answered',       cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  zero:           { label: 'Zero (attested)', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  not_applicable: { label: 'Not applicable',  cls: 'bg-slate-50 text-slate-600 border-slate-200' },
  partial:        { label: 'Partial',         cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  outstanding:    { label: 'Outstanding',     cls: 'bg-slate-50 text-slate-500 border-slate-200' },
  blocked:        { label: 'Needs attention', cls: 'bg-red-50 text-red-700 border-red-200' },
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export default function CollectionPanel() {
  const [measureId, setMeasureId] = useState('odr');
  const [cy, setCy] = useState(new Date().getFullYear());
  const [quarter, setQuarter] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await fetch(
        `${API}/api/reporter/elements/readiness?measure_id=${measureId}&cy=${cy}&quarter=${quarter}`,
        { credentials: 'include' },
      );
      if (res.status === 404) { setData(null); setError('unavailable'); return; }
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || `Could not load (${res.status}).`);
      setData(d);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [measureId, cy, quarter]);

  useEffect(() => { load(); }, [load]);

  if (error === 'unavailable') {
    return <div className="text-sm text-slate-500">Element collection is not switched on for this environment.</div>;
  }

  const s = data?.summary;

  return (
    <div>
      <div className="mb-5">
        <h2 className="text-lg font-semibold text-slate-900">Collection</h2>
        <p className="text-sm text-slate-600 max-w-3xl mt-1">
          Each number CMS requires is tracked on its own, with one owner. A vendor sees only the elements
          they are responsible for, so nobody is asked for figures they do not hold — and the period cannot
          be submitted until every element has an answer.
        </p>
      </div>

      {/* Period selector */}
      <div className="flex flex-wrap items-end gap-3 mb-6 pb-5 border-b border-slate-200">
        <Field label="Measure">
          <select value={measureId} onChange={e => setMeasureId(e.target.value)} className={SELECT}>
            {MEASURES.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
        </Field>
        <Field label="Contract year">
          <input type="number" value={cy} onChange={e => setCy(Number(e.target.value))} className={`${SELECT} w-28`} />
        </Field>
        <Field label="Quarter">
          <select value={quarter} onChange={e => setQuarter(Number(e.target.value))} className={SELECT}>
            {[1, 2, 3, 4].map(q => <option key={q} value={q}>Q{q}</option>)}
          </select>
        </Field>
        <button onClick={load} className="px-3 py-2 text-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
          Refresh
        </button>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {error && error !== 'unavailable' && (
        <div className="mb-4 border border-red-200 bg-red-50 text-red-800 text-sm px-3 py-2">{error}</div>
      )}

      {!loading && data && (
        <>
          <Headline summary={s} periodLabel={data.period_label} />

          <div className="space-y-3">
            {data.by_owner.map(o => (
              <OwnerCard
                key={o.owner}
                owner={o}
                elements={data.elements.filter(e => e.owner === o.owner)}
                measureId={measureId} cy={cy} quarter={quarter}
                expanded={expanded === o.owner}
                onToggle={() => setExpanded(expanded === o.owner ? null : o.owner)}
                onChanged={load}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Headline({ summary, periodLabel }) {
  if (!summary) return null;
  const pct = summary.percent_complete;
  const ready = summary.ready_to_submit;

  return (
    <div className={`border px-4 py-4 mb-5 ${ready ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}>
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {ready
              ? `${periodLabel} is complete and can be submitted.`
              : `${periodLabel} — ${summary.outstanding} of ${summary.required_elements} elements still outstanding.`}
          </div>
          <div className="text-[12px] text-slate-600 mt-1">
            {summary.satisfied} answered
            {summary.blocked > 0 && <> · <span className="text-red-700 font-medium">{summary.blocked} need attention</span></>}
            {summary.warnings > 0 && <> · {summary.warnings} to check</>}
          </div>
        </div>
        <div className="text-right">
          <div className={`text-2xl font-semibold ${ready ? 'text-emerald-700' : 'text-slate-900'}`}>{pct}%</div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500">complete</div>
        </div>
      </div>
      <div className="mt-3 h-1.5 bg-slate-200">
        <div className={`h-1.5 ${ready ? 'bg-emerald-600' : 'bg-red-700'}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function OwnerCard({ owner, elements, measureId, cy, quarter, expanded, onToggle, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);
  const done = owner.outstanding === 0;

  function downloadTemplate() {
    const url = `${API}/api/reporter/elements/template?measure_id=${measureId}&owner=${encodeURIComponent(owner.owner)}&cy=${cy}&quarter=${quarter}`;
    window.open(url, '_blank', 'noopener');
  }

  async function uploadReturned(file) {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('cy', String(cy));
      form.append('owner', owner.owner);
      if (owner.vendor_name) form.append('vendor_name', owner.vendor_name);

      const res = await fetch(`${API}/api/reporter/elements/upload`, {
        method: 'POST', credentials: 'include', body: form,
      });
      const d = await res.json();
      if (!res.ok) {
        setResult({ ok: false, message: d.error || 'The file was not accepted.', problems: d.problems || [] });
        return;
      }
      setResult({ ok: true, message: d.message });
      onChanged && onChanged();
    } catch (e) {
      setResult({ ok: false, message: e.message, problems: [] });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  return (
    <div className="border border-slate-200 bg-white">
      <div className="px-4 py-3 flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900">{owner.owner_label}</span>
            {owner.vendor_name && <span className="text-xs text-slate-500">{owner.vendor_name}</span>}
            <span className={`text-[11px] px-2 py-0.5 border ${done
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
              {done ? 'Complete' : `${owner.outstanding} outstanding`}
            </span>
            {owner.blocked > 0 && (
              <span className="text-[11px] px-2 py-0.5 border bg-red-50 text-red-700 border-red-200">
                {owner.blocked} need attention
              </span>
            )}
          </div>
          <div className="text-[12px] text-slate-600 mt-1">
            {owner.satisfied} of {owner.total} elements answered
            {owner.contact_email && <> · <a className="underline" href={`mailto:${owner.contact_email}`}>{owner.contact_email}</a></>}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button onClick={downloadTemplate}
            className="px-3 py-1.5 text-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
            Download their template
          </button>
          <label className={`px-3 py-1.5 text-sm border cursor-pointer ${busy
            ? 'border-slate-200 text-slate-400'
            : 'border-red-700 text-red-700 hover:bg-red-50'}`}>
            {busy ? 'Reading…' : 'Upload returned file'}
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" disabled={busy}
              onChange={e => uploadReturned(e.target.files?.[0])} />
          </label>
          <button onClick={onToggle} className="px-2 py-1.5 text-sm text-slate-500 hover:text-slate-900">
            {expanded ? 'Hide' : 'Details'}
          </button>
        </div>
      </div>

      {result && (
        <div className={`mx-4 mb-3 border px-3 py-2 text-sm ${result.ok
          ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
          : 'border-red-200 bg-red-50 text-red-800'}`}>
          <div>{result.message}</div>
          {result.problems?.length > 0 && (
            <ul className="mt-2 space-y-1 text-[12px] list-disc pl-4">
              {result.problems.slice(0, 25).map((p, i) => <li key={i}>{p.error}</li>)}
              {result.problems.length > 25 && <li>…and {result.problems.length - 25} more.</li>}
            </ul>
          )}
        </div>
      )}

      {expanded && <ElementTable elements={elements} />}
    </div>
  );
}

function ElementTable({ elements }) {
  return (
    <div className="border-t border-slate-100 overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-slate-500 border-b border-slate-100">
            <th className="px-4 py-2 font-medium">Element</th>
            <th className="px-3 py-2 font-medium w-32">Status</th>
            <th className="px-3 py-2 font-medium w-24 text-right">Figure</th>
            <th className="px-4 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {elements.map(e => {
            const st = STATE_STYLE[e.state] || STATE_STYLE.outstanding;
            return (
              <tr key={e.element_key} className="border-b border-slate-50 align-top">
                <td className="px-4 py-2">
                  <div className="text-slate-900">{e.field_name}</div>
                  <div className="text-slate-400 font-mono text-[11px]">{e.element_path}</div>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[11px] px-2 py-0.5 border ${st.cls}`}>{st.label}</span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-slate-900">
                  {e.submits_as != null ? Number(e.submits_as).toLocaleString() : '—'}
                </td>
                <td className="px-4 py-2 text-slate-600">
                  {e.blocked_reason && <div className="text-red-700">{e.blocked_reason}</div>}
                  {e.missing_months?.length > 0 && e.state !== 'outstanding' && (
                    <div>Waiting on {e.missing_months.map(m => MONTHS[m - 1]).join(', ')}.</div>
                  )}
                  {e.warnings?.map((w, i) => <div key={i} className="text-amber-700">{w}</div>)}
                  {e.attestation_note && <div className="italic">“{e.attestation_note}”</div>}
                  {e.last_submitted_by && (
                    <div className="text-slate-400">
                      {e.last_submitted_by}
                      {e.last_submitted_at ? ` · ${new Date(e.last_submitted_at).toLocaleDateString()}` : ''}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const SELECT = 'border border-slate-300 px-3 py-2 text-sm text-slate-900 bg-white';

function Field({ label, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}
